import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

/**
 * varAlpha - Creates alpha transparency using color channels
 * Enables systematic alpha transparency across the design system
 * @param {string} channel - RGB color channel (e.g., '0 167 111')
 * @param {number} opacity - Opacity value 0-1
 * @returns {string} - rgba color string
 */
export const varAlpha = (channel, opacity) => {
  return `rgba(${channel} / ${opacity})`;
};

/**
 * createPaletteChannel - Converts hex color to RGB channel
 * Used for varAlpha color system
 * @param {string} color - Hex color (e.g., '#00A76F')
 * @returns {string} - RGB channel string (e.g., '0 167 111')
 */
const createPaletteChannel = (color) => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `${r} ${g} ${b}`;
};

// ----------------------------------------------------------------------

// SETUP COLORS

const GREY = {
  0: '#FFFFFF',
  100: '#F9FAFB',
  200: '#F4F6F8',
  300: '#DFE3E8',
  400: '#C4CDD5',
  500: '#919EAB',
  600: '#637381',
  700: '#454F5B',
  800: '#212B36',
  900: '#161C24',
  // RGB channels for alpha transparency (used with varAlpha)
  '500Channel': createPaletteChannel('#919EAB'), // '145 158 171'
};

const PRIMARY = {
  lighter: '#C8FAD6',
  light: '#5BE49B',
  main: '#00A76F',
  dark: '#007867',
  darker: '#004B50',
  contrastText: '#FFFFFF',
  mainChannel: createPaletteChannel('#00A76F'), // '0 167 111'
};

const SECONDARY = {
  lighter: '#EFD6FF',
  light: '#C684FF',
  main: '#8E33FF',
  dark: '#5119B7',
  darker: '#27097A',
  contrastText: '#FFFFFF',
  mainChannel: createPaletteChannel('#8E33FF'), // '142 51 255'
};

const INFO = {
  lighter: '#CAFDF5',
  light: '#61F3F3',
  main: '#00B8D9',
  dark: '#006C9C',
  darker: '#003768',
  contrastText: '#FFFFFF',
  mainChannel: createPaletteChannel('#00B8D9'), // '0 184 217'
};

const SUCCESS = {
  lighter: '#D3FCD2',
  light: '#77ED8B',
  main: '#22C55E',
  dark: '#118D57',
  darker: '#065E49',
  contrastText: '#ffffff',
  mainChannel: createPaletteChannel('#22C55E'), // '34 197 94'
};

const WARNING = {
  lighter: '#FFF5CC',
  light: '#FFD666',
  main: '#FFAB00',
  dark: '#B76E00',
  darker: '#7A4100',
  contrastText: GREY[800],
  mainChannel: createPaletteChannel('#FFAB00'), // '255 171 0'
};

const ERROR = {
  lighter: '#FFE9D5',
  light: '#FFAC82',
  main: '#FF5630',
  dark: '#B71D18',
  darker: '#7A0916',
  contrastText: '#FFFFFF',
  mainChannel: createPaletteChannel('#FF5630'), // '255 86 48'
};

const COMMON = {
  common: {
    black: '#000000',
    white: '#FFFFFF',
  },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  divider: alpha(GREY[500], 0.2),
  action: {
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

export const light = {
  ...COMMON,
  alternate: {
    main: '#f7faff',
    dark: '#edf1f7',
  },
  mode: 'light',
  text: {
    primary: GREY[800],
    secondary: GREY[600],
    disabled: GREY[500],
  },
  background: {
    paper: '#FFFFFF',
    default: GREY[100],
    neutral: GREY[200],
  },
  action: {
    ...COMMON.action,
    active: GREY[600],
  },
};

export const dark = {
  ...COMMON,
  alternate: {
    main: '#1a2138',
    dark: '#151a30',
  },
  mode: 'dark',
  text: {
    primary: '#FFFFFF',
    secondary: GREY[500],
    disabled: GREY[600],
  },
  background: {
    paper: GREY[800],
    default: GREY[900],
    neutral: alpha(GREY[500], 0.12),
  },
  action: {
    ...COMMON.action,
    active: GREY[500],
  },
};
